import { supabase } from "@/ajax/clients/supabase";
import { SocialLogin } from "@capgo/capacitor-social-login";
import { getEnvVar, isNative } from "@/common/utils/environment";
import { getWebStorage } from "@/common/utils/storage";
import posthog from "posthog-js";

let initialized: Promise<void> | null = null;

/**
 * The web build of @capgo/capacitor-social-login reads `localStorage` while its
 * plugin instance is being constructed, so the first call into it throws in
 * embedded WebKit contexts that expose no web storage at all (Apple Mail's link
 * preview renderer, sandboxed iframes). Because this function is invoked without
 * awaiting — at module load below, and from the sign-in page's effect — that
 * throw surfaced as an unhandled rejection.
 *
 * Google OAuth cannot complete without somewhere to persist the session, so
 * there is nothing to salvage in those contexts: skip initialization entirely.
 * Native platforms always have web storage in their webview and use the plugin's
 * native implementation, so they are never gated on this.
 */
const canInitializeGoogleAuth = () => isNative() || getWebStorage() !== null;

export const initializeGoogleAuth = async () => {
  if (!canInitializeGoogleAuth()) {
    return;
  }
  if (!initialized) {
    initialized = SocialLogin.initialize({
      google: {
        iOSClientId: getEnvVar("VITE_PUBLIC_GOOGLE_AUTH_IOS_CLIENT_ID"),
        webClientId: getEnvVar("VITE_PUBLIC_GOOGLE_AUTH_CLIENT_ID"),
        mode: "online",
        redirectUrl: `${window.location.origin}/auth/signin/oauth`,
      },
    });
  }
  return initialized;
};

/**
 * Prepares the plugin ahead of the user's tap. Nothing awaits this, so a
 * failure (the web implementation is a lazily loaded chunk that can fail to
 * fetch, and its constructor can throw in restricted webviews) must not escape
 * as an unhandled rejection. The failed attempt is discarded so the sign-in tap
 * retries initialization and reports its own error to the user.
 */
export const prewarmGoogleAuth = () => {
  initializeGoogleAuth().catch(() => {
    initialized = null;
  });
};

const nativeSignInWithGoogle = async () => {
  try {
    await initializeGoogleAuth();
    const response = await SocialLogin.login({
      provider: "google",
      options: {
        scopes: ["email", "profile"],
        forceRefreshToken: true,
        forcePrompt: true, // if you need refresh token
      },
    });

    const idToken = (response.result as { idToken: string }).idToken;

    if (!idToken) {
      throw new Error("No authentication data present!");
    }

    const { data, error } = await supabase.auth.signInWithIdToken({
      provider: "google",
      token: idToken,
    });

    if (error) {
      throw error;
    }

    return { error, data };
  } catch (error) {
    console.error("Error signing in with Google:", error);
    return { error, data: null };
  }
};

/***
 * Sign in with Google email. This redirects the user to Google auth and they are redirected back when done.
 * @returns
 */
const _webSignInWithGoogle = async ({
  redirectTo,
}: {
  redirectTo?: string;
} = {}) => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo,
    },
  });

  return {
    data,
    error,
  };
};

export const signInWithGoogle = async () => {
  posthog.capture("signin.google.attempt");
  return nativeSignInWithGoogle();
};

prewarmGoogleAuth();
